import { StateOptions, CountryOptions, TimezoneOptions, StatusReasonOptions } from "./options";

export function formatDateToLong(dateString) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dateParts = dateString.split("-");
  const year = dateParts[0];
  const monthIndex = parseInt(dateParts[1]) - 1;
  const day = dateParts[2];

  const formattedDate = `${day} ${months[monthIndex]} ${year}`;
  return formattedDate;
}

export function formatUTCDate(inputDate) {
  // Parse the input date string into a Date object
  const date = new Date(inputDate);

  // Extract the year, month, and day from the Date object
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  // Define an array of month names
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Create the formatted date string
  const formattedDate = `${day} ${monthNames[month]} ${year}`;

  return formattedDate;
}

// Date formatting
// ------------------------------------------------------------

// DAY
export const formatDateToDDMMYYYY = (dateString) => {
  const today = new Date(dateString);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  const formattedDate = `${day}/${month}/${year}`;
  return formattedDate;
};

/**
 * Picks the first date candidate suitable for display/filter (skips invalid parses and
 * sentinel placeholders often stored as doseDate, e.g. year 0001, so startDate/created can apply).
 */
export const pickFirstValidServiceDate = (...candidates) => {
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    const y = d.getUTCFullYear();
    if (y < 1900 || y > 2200) continue;
    return raw;
  }
  return null;
};

export const formatDateToDD_MMM_YYYY = (dateInput) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "N/A";
  const year = date.getUTCFullYear();
  const monthIndex = date.getUTCMonth();
  const day = String(date.getUTCDate()).padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const formattedDate = `${day}-${months[monthIndex]}-${year}`;
  return formattedDate;
};

export const formatTimeFromISO = (dateInput = "") => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const toValidNoteDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (!(date instanceof Date) || Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 2000) {
    return null;
  }
  return date;
};

export const getNoteDateForFilter = (note) => {
  const systemPeriod = note?.systemPeriod || note?.SystemPeriod;
  const systemValue = typeof systemPeriod === "string"
    ? systemPeriod
    : (systemPeriod?.LowerBound || systemPeriod?.lowerBound);

  const fallbackValue = note?.date || note?.Date || note?.createdAt || note?.CreatedAt;
  return toValidNoteDate(systemValue) ?? toValidNoteDate(fallbackValue);
};

/**
 * Filters an array of notes by a serviceDate (YYYY-MM-DD) day-range.
 * Returns the original source when `serviceDate` is falsy.
 */
export const filterNotesByServiceDate = (source = [], serviceDate) => {
  if (!serviceDate) return source;
  const start = new Date(serviceDate + 'T00:00:00Z');
  const end = new Date(serviceDate + 'T23:59:59.999Z');
  return (source || []).filter(n => {
    const d = getNoteDateForFilter(n);
    return d && d >= start && d <= end;
  });
};

/**
 * Parses Auth0 error responses and extracts the most relevant error message
 * @param {Object} error - The error object from Auth0 API calls
 * @param {Object} error.response - The response object containing error details
 * @param {Object} error.response.data - The error data object
 * @param {string} error.response.data.message - The primary error message
 * @param {string} error.response.data.error - The error type
 * @param {string} error.response.data.errorCode - The specific error code
 * @param {number} error.response.status - HTTP status code
 * @param {string} fallbackMessage - Optional fallback message if no error details found
 * @returns {string} The most relevant error message for user display
 */
export const parseAuth0Error = (error, fallbackMessage = 'An error occurred. Please try again.') => {
  // Check if error has response data with message
  if (error?.response?.data?.message) {
    const message = error.response.data.message;
    
    // Check if the message contains JSON-like content that needs parsing
    if (typeof message === 'string' && message.includes('{') && message.includes('}')) {
      try {
        // Try to extract JSON from the message string
        const jsonStart = message.indexOf('{');
        const jsonEnd = message.lastIndexOf('}') + 1;
        const jsonString = message.substring(jsonStart, jsonEnd);
        const parsedData = JSON.parse(jsonString);
        
        // Return the actual message from the parsed JSON
        if (parsedData.message) {
          return parsedData.message;
        }

        if(parsedData.error_description) {
          return parsedData.error_description;
        }
        
        // If no message in parsed data, return the original message
        return message;
      } catch (parseError) {
        // If JSON parsing fails, return the original message
        return message;
      }
    }
    
    // Return the message as-is if it doesn't contain JSON
    return message;
  }
  
  // Check for specific error codes and provide user-friendly messages
  if (error?.response?.data?.errorCode) {
    const errorCode = error.response.data.errorCode;
    
    switch (errorCode) {
      case 'inexistent_user':
        return 'The user account does not exist. Please check your credentials and try again.';
      case 'invalid_grant':
        return 'Invalid credentials. Please check your username and password.';
      case 'invalid_token':
        return 'Your session has expired. Please log in again.';
      case 'insufficient_scope':
        return 'You do not have permission to perform this action.';
      case 'invalid_request':
        return 'Invalid request. Please check your input and try again.';
      case 'server_error':
        return 'A server error occurred. Please try again later.';
      case 'temporarily_unavailable':
        return 'The service is temporarily unavailable. Please try again later.';
      default:
        // If we have an error code but no specific message, use the error type
        if (error.response.data.error) {
          return `${error.response.data.error}. Please try again.`;
        }
    }
  }
  
  // Check for HTTP status codes and provide appropriate messages
  if (error?.response?.status) {
    const status = error.response.status;
    
    switch (status) {
      case 400:
        return 'Bad request. Please check your input and try again.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'Conflict. The resource already exists or cannot be created.';
      case 422:
        return 'Validation error. Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Internal server error. Please try again later.';
      case 502:
        return 'Bad gateway. Please try again later.';
      case 503:
        return 'Service unavailable. Please try again later.';
      case 504:
        return 'Gateway timeout. Please try again later.';
      default:
        if (status >= 500) {
          return 'Server error. Please try again later.';
        } else if (status >= 400) {
          return 'Request error. Please check your input and try again.';
        }
    }
  }
  
  // Check for error type if no specific error code or status
  if (error?.response?.data?.error) {
    return `${error.response.data.error}. Please try again.`;
  }
  
  // Check for generic error message
  if (error?.message) {
    return error.message;
  }
  
  // Return fallback message if no error details found
  return fallbackMessage;
};

// MONTH
export const formatDateToMMYY = (dateString) => {
  const d = new Date(dateString);
  const year = d.getFullYear().toString().substring(2);
  const month = String(d.getMonth() + 1).padStart(2, "0");

  const formattedDate = `${month}/${year}`;
  return formattedDate;
};

// YEAR
export const formatDateToYYYYMMDD = (dateString) => {
  const today = new Date(dateString);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export function formatToYYYY_MMM_DD(dateString = "") {
  if (!dateString) return "";

  const [date] = dateString.split("T");
  const [year, month, day] = date.split("-");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const outputDate = `${year}-${monthNames[parseInt(month) - 1]}-${day}`;
  return outputDate;
}

export function formatToMonthDayYear(dateString = "") {
  if (!dateString) return "";

  const [date] = dateString.split("T");
  const [year, month, day] = date.split("-");
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  
  const monthName = monthNames[parseInt(month) - 1];
  const dayNumber = parseInt(day);
  
  return `${monthName} ${dayNumber}, ${year}`;
}

export function formatToYYYYMMDDBackend(dateString = "") {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-");
  const outputDate = `${year}-${month}-${day}`;
  return outputDate;
}

export function convertToYYYYMMDD(isoDateString) {
  const date = new Date(isoDateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

// End date formatting
//---------------------------------------------------------------

export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) {
    return "";
  }
  // Remove any non-digit characters from the phone number
  const cleanedNumber = phoneNumber.replace(/\D/g, "");

  // Extract the area code and remaining digits
  const areaCode = cleanedNumber.substring(0, 3);
  const remainingDigits = cleanedNumber.substring(3);

  // Format the phone number in (areacode) XXX-XXXX format
  const formattedNumber = `(${areaCode}) ${remainingDigits.substring(
    0,
    3
  )}-${remainingDigits.substring(3)}`;

  return formattedNumber;
}

export function addWeeksToDate(dateString = "", weeks = 0) {
  const date = new Date(dateString);
  date.setDate(date.getDate() + weeks * 7);
  return date.toISOString().split("T")[0];
}

function getCalendarDaysSinceBirth(birthDate, currentDate) {
  const utc1 = Date.UTC(
    birthDate.getFullYear(),
    birthDate.getMonth(),
    birthDate.getDate()
  );
  const utc2 = Date.UTC(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );
  return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
}

const NON_FOLLOW_UP_OVERRIDES = new Set(["modify", "modify-withoutnotify"]);

const normalizePmrInterventionToken = (value = "") =>
  String(value).trim().toLowerCase().replace(/[;,&\s]/g, "");

const isResolvedInterventionStatus = (status = "") => {
  const normalizedStatus = normalizePmrInterventionToken(status);
  return normalizedStatus === "completed" || normalizedStatus === "cancelled";
};

/** PMR is "in review" only when there are unresolved interventions that require follow-up; explicit non-follow-up overrides (modify / modify-withoutNotify) do not trigger watermark. */
export const hasUnresolvedFollowUpInterventions = (medicationsObj) => {
  const medications = Array.isArray(medicationsObj?.medications)
    ? medicationsObj.medications
    : [];

  return medications.some((medication) => {
    const intervention = medication?.intervention;
    if (!intervention) return false;

    if (
      isResolvedInterventionStatus(intervention?.status || medication?.status)
    ) {
      return false;
    }

    const override = normalizePmrInterventionToken(intervention?.override);
    if (NON_FOLLOW_UP_OVERRIDES.has(override)) return false;

    return true;
  });
};

export function calculateAge(dateOfBirthString) {
  if (!dateOfBirthString) {
    return ``;
  }
  const birthDate = new Date(dateOfBirthString);
  const currentDate = new Date();

  if (Number.isNaN(birthDate.getTime())) {
    return ``;
  }

  const daysSinceBirth = getCalendarDaysSinceBirth(birthDate, currentDate);
  if (daysSinceBirth < 0) {
    return ``;
  }
  // 0-28 days -> days
  if (daysSinceBirth <= 28) {
    return `${daysSinceBirth} day${daysSinceBirth !== 1 ? "s" : ""}`;
  }
  // 1-12 weeks -> weeks + days
  if (daysSinceBirth <= 84) {
    const weeks = Math.floor(daysSinceBirth / 7);
    const days = daysSinceBirth % 7;
    if (days === 0) {
      return `${weeks} week${weeks !== 1 ? "s" : ""}`;
    }
    return `${weeks} week${weeks !== 1 ? "s" : ""} ${days} day${
      days !== 1 ? "s" : ""
    }`;
  }

  let years = currentDate.getFullYear() - birthDate.getFullYear();
  let months = currentDate.getMonth() - birthDate.getMonth();

  // Adjust for negative months (current month < birth month)
  if (months < 0) {
    years--;
    months += 12;
  }

  if (Number.isNaN(years) || Number.isNaN(months)) {
    return ``;
  }
  if (years < 0) {
    return ``;
  }
  // If birthday day has not occurred this month, current month is not complete.
  if (currentDate.getDate() < birthDate.getDate()) {
    months--;
    if (months < 0) {
      months += 12;
      years--;
    }
  }
  if (years < 0) {
    return ``;
  }
  const totalMonths = years * 12 + months;

  // Healthcare standard: show age in months up to 24 months (2 years).
  if (totalMonths < 24) {
    return `${totalMonths} month${totalMonths !== 1 ? "s" : ""}`;
  }

  // 24+ months -> years only (no trailing months)
  return `${years} year${years !== 1 ? "s" : ""}`;
}

export function calculateAgeYear(dateOfBirthString) {
  if (!dateOfBirthString) {
    return 0;
  }
  const birthDate = new Date(dateOfBirthString);
  const currentDate = new Date();

  let years = currentDate.getFullYear() - birthDate.getFullYear();
  let months = currentDate.getMonth() - birthDate.getMonth();

  // Adjust for negative months (current month < birth month)
  if (months < 0) {
    years--;
    months += 12;
  }

  // Adjust for cases where the birth date hasn't occurred this month yet
  if (
    currentDate.getDate() < birthDate.getDate() &&
    currentDate.getMonth() === birthDate.getMonth()
  ) {
    months--;
    if (months < 0) {
      months += 12;
      years--;
    }
  }
  if (Number.isNaN(years) || Number.isNaN(months) || years < 0) {
    return 0;
  }
  return years;
}

export function extractPhoneNumber(inputPhoneNumber) {
  // Remove non-numeric characters
  const numericPhoneNumber = inputPhoneNumber?.replace(/\D/g, "");
  return numericPhoneNumber;
}

export function formatHealth(phoneNumber) {
  const cleaned = ("" + phoneNumber).replace(/\D/g, ""); // Remove non-digits
  const match = cleaned.match(/^(\d{4})(\d{3})(\d{3})$/); // Split into groups

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  // Return the original input if it doesn't match the expected format
  return phoneNumber;
}

export function formatPhone(phoneNumber) {
  const cleaned = ("" + phoneNumber).replace(/\D/g, ""); // Remove non-digits
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/); // Split into groups

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  // Return the original input if it doesn't match the expected format
  return phoneNumber;
}


export function formatPhoneDocumentation(phoneNumber) {
  const cleaned = ("" + phoneNumber).replace(/\D/g, ""); // Remove non-digits
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/); // Split into groups

  if (match) {
    return `+1 ${match[1]}-${match[2]}-${match[3]}`;
  }

  // Return the original input if it doesn't match the expected format
  return phoneNumber;
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function capitalizeFirstLetter(string) {
  if (string && string.length > 0)
    return string.charAt(0).toUpperCase() + string.slice(1);
  else return "";
}

export function capitalizeNameWithSpecialChars(string) {
  if (!string || string.length === 0) return "";
  
  // Capitalize first letter
  let result = string.charAt(0).toUpperCase() + string.slice(1);
  
  // Capitalize letters after apostrophes and hyphens (with optional spaces)
  // This handles cases like: d'alosio -> D'Alosio, smith -jackson -> Smith -Jackson
  result = result.replace(/(['-])\s*([a-z])/gi, (match, char, letter) => {
    return char + letter.toUpperCase();
  });
  
  return result;
}

export function capitalizeCustomText(element) {
  const smallWords = [
    "and",
    "or",
    "the",
    "a",
    "an",
    "in",
    "on",
    "at",
    "by",
    "to",
    "with",
    "of",
  ];

  // Split the text content into words
  const words = element.textContent.split(" ");

  // Capitalize each word except for the small words
  const capitalizedWords = words.map((word) => {
    if (smallWords.includes(word.toLowerCase())) {
      return word; // Keep small words as they are
    } else {
      return word.charAt(0).toUpperCase() + word.slice(1); // Capitalize other words
    }
  });

  // Join the words back together and set the text content
  element.textContent = capitalizedWords.join(" ");
}

export function convertToPascalCase(string) {
  if (!string || string.length === 0) return "";

  let s = String(string).toLowerCase();

  s = s.replace(/(^|[\s'\-])([a-z])/g, (match, sep, chr) => {
    return sep + chr.toUpperCase();
  });

  return s;
}

export function convertHeight(height, fromUnit, toUnit) {
  if (!(fromUnit && fromUnit !== null)) {
    return height;
  }
  const conversionFactors = {
    in: {
      cm: 2.54,
      in: 1,
    },
    cm: {
      in: 1 / 2.54,
      cm: 1,
    },
  };

  if (!conversionFactors[fromUnit] || !conversionFactors[toUnit]) {
    throw new Error(
      "Invalid unit provided. Use 'in' for inches or 'cm' for centimeters."
    );
  }

  // Convert height to the target unit
  return (height * conversionFactors[fromUnit][toUnit]).toFixed(0);
}

export function convertWeight(weight, fromUnit, toUnit) {
  if (!(fromUnit && fromUnit !== null)) {
    return weight;
  }
  
  // If converting to the same unit, return the weight as is
  if (fromUnit === toUnit) {
    return weight;
  }
  
  const decimalPointsToUse = getDecimalPlaces(weight);
  // Input validation (optional)
  if (isNaN(weight) || weight <= 0) {
    return "Invalid weight value.";
  }

  const conversionFactors = {
    kg: { lbs: 2.2046, g: 1000, kg: 1 },
    lbs: { kg: 0.45359237, g: 453.592, lbs: 1 },
    g: { kg: 0.001, lbs: 0.0022046, g: 1 },
  };

  if (!conversionFactors[fromUnit] || !conversionFactors[fromUnit][toUnit]) {
    return "Invalid unit combination. Please use 'kg', 'lbs', or 'g'.";
  }

  const convertedWeight = weight * conversionFactors[fromUnit][toUnit];
  return convertedWeight.toFixed(decimalPointsToUse);
}

/**
 * Normalizes frontend source values to backend expected values
 * Converts: family_caregiver -> caregiver, primary_provider -> physician, etc.
 * @param {string} sourceValue - Frontend source value
 * @returns {string} Backend normalized value
 */
export function normalizeSourceValue(sourceValue) {
  if (!sourceValue) return "patient";
  // Backend expects: patient, caregiver, physician, other
  const mapping = {
    "family_caregiver": "caregiver",
    "primary_provider": "physician",
    "nurse_practitioner": "physician",
    "pharmacist": "other",
  };
  return mapping[sourceValue] || sourceValue;
}

/**
 * Denormalizes backend source values back to frontend values
 * Converts: caregiver -> family_caregiver, physician -> primary_provider, etc.
 * Used when editing/displaying data from backend
 * @param {string} sourceValue - Backend normalized value
 * @returns {string} Frontend source value
 */
export function denormalizeSourceValue(sourceValue) {
  if (!sourceValue) return "patient";
  // Map backend values back to frontend values
  const reverseMapping = {
    "caregiver": "family_caregiver",
    "physician": "primary_provider", // Default to primary_provider if physician
    "other": "pharmacist", // Default to pharmacist if other
  };
  return reverseMapping[sourceValue] || sourceValue;
}

export function getDecimalPlaces(number) {
  // Handle non-numeric input (optional)
  if (isNaN(number)) {
    return "Not a number";
  }

  // Convert the number to a string
  const stringNumber = number.toString();

  // Check if the number has a decimal point
  if (!stringNumber.includes(".")) {
    return 0;
  }

  // Split the string at the decimal point and return the length of the decimal part
  return stringNumber.split(".")[1].length;
}

export const findLastIndex = (array, predicate) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i])) {
      return i;
    }
  }
  return -1;
};

export const scrollX = (id, x = null) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollBy({
      left: x !== null ? x : 480,
      behavior: "smooth", // Add smooth scrolling animation
    });
  }
};

export const scrollY = (id, x = null) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollBy({
      top: x !== null ? x : 480,
      behavior: "smooth", // Add smooth scrolling animation
    });
  }
};

export const compareDates = (date1, date2) => {
  // Convert the dates to only compare the year, month, and day
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  const dateOnly1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const dateOnly2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

  // Compare the two dates
  if (dateOnly1 > dateOnly2) {
    return 1; // date1 is later than date2
  } else if (dateOnly1 < dateOnly2) {
    return -1; // date1 is earlier than date2
  } else {
    return 0; // both dates are the same
  }
};
/**
 * Helper function to get state label from state value
 * @param {string} stateValue - The state value to look up
 * @returns {string} The corresponding state label or empty string if not found
 */
export const getStateLabel = (stateValue) => {
  const state = StateOptions.find(option => option.value === stateValue);
  return state ? state.label : '';
};

export const getCountryLabel = (countryValue) => {
  const country = CountryOptions.find(option => option.value === countryValue);
  return country ? country.label : '';
};

export const getTimeZoneLabel = (timeZoneValue) => {
  const timeZone = TimezoneOptions.find(option => option.value === timeZoneValue);
  return timeZone ? timeZone.label : '';
};

export const getStatusReasonLabel = (statusReasonValue) => {
  const statusReason = StatusReasonOptions.find(option => option.value === statusReasonValue);
  return statusReason ? statusReason.label : '';
};

export const getTimeDifference = (startDateTime, endDateTime) => {
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);

  const diffMs = end - start; // difference in milliseconds

  // Convert to hours, minutes, seconds
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  // Format as hh:mm:ss
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0')
  ].join(':');
};

/**
 * Helper: Format date to ISO string (YYYY-MM-DD)
 * @param {Date|string|null|undefined} dateValue - The date value to format
 * @returns {string} ISO formatted date string (YYYY-MM-DD)
 */
export const formatDateToISO = (dateValue) => {
  const date = new Date(dateValue || new Date());
  return isNaN(date.getTime()) ? new Date().toISOString().split('T')[0] : date.toISOString().split('T')[0];
};

/**
 * Splits a string into lines with a fixed number of words per line.
 * Useful for rendering tooltips that need controlled widths.
 *
 * @param {string} text - The text to format.
 * @param {number} wordsPerLine - Number of words per line.
 * @returns {string} - The formatted text with newline separators.
 */
export const formatWordsIntoLines = (text = "", wordsPerLine = 8) => {
  if (!text) return "";
  const words = text.split(/\s+/);
  const lines = [];
  for (let i = 0; i < words.length; i += wordsPerLine) {
    lines.push(words.slice(i, i + wordsPerLine).join(" "));
  }
  return lines.join("\n");
};

// Date handling utilities for backend compatibility
export const DATE_TEXT_OPTIONS = ["Date Not Known", "Date Not Specified", "Less than 3 months", "3 to 6 months", "6 months to 1 year", "Greater than one year"];
export const DATE_CALENDAR_OPTIONS = ["(YYYY)", "(MM/YYYY)", "(DD/MM/YYYY)"];
export const DATE_OPTION_LABELS = {
  "(YYYY)": "Select Date - Year only",
  "(MM/YYYY)": "Select Date - Month, Year",
  "(DD/MM/YYYY)": "Select Date - Specific Date"
};

export const formatDateForBackend = (date) => {
  if (!date) return "";
  if (date.match(/^\d{4}$/)) return `${date}-01-01`;
  if (date.match(/^\d{4}-\d{2}$/)) return `${date}-01`;
  if (date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
  const parsed = new Date(date);
  return !isNaN(parsed.getTime()) ? parsed.toISOString().split('T')[0] : "";
};

export const handleDateOptionChange = (dateVal, dateOptionVal, setDateFn, setDateOptionFn) => {
  if (dateOptionVal && DATE_TEXT_OPTIONS.includes(dateOptionVal)) {
    setDateFn(null);
    setDateOptionFn(dateOptionVal);
    return;
  }
  
  if (dateOptionVal && DATE_CALENDAR_OPTIONS.includes(dateOptionVal) && !dateVal) {
    setDateFn(null);
    setDateOptionFn(dateOptionVal);
    return;
  }
  
  if (dateOptionVal && DATE_CALENDAR_OPTIONS.includes(dateOptionVal) && dateVal) {
    setDateFn(formatDateForBackend(dateVal));
    setDateOptionFn(dateOptionVal);
    return;
  }
  
  setDateFn(formatDateForBackend(dateVal));
  setDateOptionFn(dateVal ? null : null);
};

export const formatDiagnosisDateForDisplay = (dateString, dateOption) => {
  if (dateOption && DATE_TEXT_OPTIONS.includes(dateOption)) return dateOption;
  if (dateOption && DATE_CALENDAR_OPTIONS.includes(dateOption) && !dateString) {
    return DATE_OPTION_LABELS[dateOption] || dateOption;
  }
  if (!dateString) return "Not Specified";
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Not Specified";
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Calculates the scroll position to center a card in a horizontal scrollable container
 * @param {number} cardIndex - The index of the card to center (0-based)
 * @param {number} cardWidth - The width of each card in pixels
 * @param {number} containerWidth - The width of the scrollable container in pixels
 * @param {number} containerScrollWidth - The total scrollable width of the container in pixels
 * @param {number} cardMargin - The margin between cards in pixels (default: 48 for mx-6)
 * @returns {number} The target scroll position to center the card
 */
export const calculateCenteredScrollPosition = (
  cardIndex,
  cardWidth,
  containerWidth,
  containerScrollWidth,
  cardMargin = 48
) => {
  // Calculate the card's left position (including margins)
  // First card has no left margin, subsequent cards have full margin
  const cardLeft = cardIndex === 0 
    ? 0 
    : cardIndex * cardWidth + (cardIndex * cardMargin);
  
  // Calculate the center position of the container
  const containerCenter = containerWidth / 2;
  
  // Calculate target scroll to center the card
  const targetScroll = cardLeft + (cardWidth / 2) - containerCenter;
  
  // Ensure we don't scroll past the boundaries
  const maxScroll = Math.max(0, containerScrollWidth - containerWidth);
  const finalScroll = Math.max(0, Math.min(targetScroll, maxScroll));
  
  return finalScroll;
};

 /* Calculates constrained dimensions and adjusted position for resizable modals
 * to ensure they stay within viewport bounds with a margin
 * 
 * @param {Object} params - Parameters object
 * @param {number} params.sizeWidth - New width from resize event
 * @param {number} params.sizeHeight - New height from resize event
 * @param {Array<number>} params.maxConstraints - [maxWidth, maxHeight] constraints
 * @param {Object} params.currentPosition - Current position {x, y}
 * @param {number} params.margin - Margin from viewport edges (default: 20)
 * @param {number} params.topMargin - Optional separate top margin to account for banner/header (default: uses margin)
 * @returns {Object} - { constrainedWidth, constrainedHeight, newPosition: {x, y} }
 */
export const calculateResizeBounds = ({
  sizeWidth,
  sizeHeight,
  maxConstraints,
  currentPosition,
  margin = 20,
  topMargin = null, // Optional: separate top margin to account for banner/header
}) => {
  const constrainedWidth = Math.min(sizeWidth, maxConstraints[0]);
  const constrainedHeight = Math.min(sizeHeight, maxConstraints[1]);

  const centerX = window.innerWidth / 2;
  const centerY = window.innerHeight / 2;

  let newX = currentPosition.x;
  let newY = currentPosition.y;

  const modalTop = centerY + newY - constrainedHeight / 2;
  const modalBottom = centerY + newY + constrainedHeight / 2;
  const modalLeft = centerX + newX - constrainedWidth / 2;
  const modalRight = centerX + newX + constrainedWidth / 2;

  // Use topMargin if provided, otherwise use regular margin
  const topMarginValue = topMargin !== null ? topMargin : margin;

  if (modalTop < topMarginValue) {
    newY = topMarginValue - centerY + constrainedHeight / 2;
  }
  if (modalBottom > window.innerHeight - margin) {
    newY = window.innerHeight - margin - centerY - constrainedHeight / 2;
  }
  if (modalLeft < margin) {
    newX = margin - centerX + constrainedWidth / 2;
  }
  if (modalRight > window.innerWidth - margin) {
    newX = window.innerWidth - margin - centerX - constrainedWidth / 2;
  }

  return {
    constrainedWidth,
    constrainedHeight,
    newPosition: { x: newX, y: newY },
  };
};